<div
  class="rounded-md border border-gray-150 bg-white shadow-sm"
  [attr.aria-busy]="isLoading"
>
  @if (isLoading) {
    <div
      class="flex h-40 items-center justify-center"
      role="status"
      aria-live="polite"
      aria-label="Loading device events"
    >
      <mat-progress-spinner
        mode="indeterminate"
        color="primary"
        diameter="40"
      ></mat-progress-spinner>
    </div>
  } @else {
    <div class="border-gray-150 border-b bg-white px-4 py-3">
      <div
        class="lg2:flex-row lg2:items-center lg2:justify-between flex flex-col gap-3"
      >
        <div class="lg2:max-w-xs relative w-full">
          <mat-icon
            class="pointer-events-none absolute top-1/2 left-3 !flex !h-4 !w-4 -translate-y-1/2 !items-center !justify-center !text-lg text-gray-500"
            aria-hidden="true"
            >search</mat-icon
          >
          <label for="qoe-device-search" class="sr-only">Search device</label>
          <input
            id="qoe-device-search"
            type="search"
            class="comcast-input !mt-0 pl-9 !text-sm"
            (input)="
              searchTerm$.set(
                $safeNavigationMigration($event.target['value']?.toLowerCase())
              )
            "
            placeholder="Search device"
            autocomplete="off"
          />
        </div>
        <div class="flex items-center gap-4">
          <div
            class="flex flex-wrap"
            role="group"
            aria-label="Filter by severity level"
          >
            @for (level of severityCheckboxes | keyvalue; track level.key) {
              <button
                type="button"
                (click)="level.value.set(!level.value())"
                class="font-lato inline-flex cursor-pointer items-center border-b-4 px-3 pt-0.5 pb-1 text-sm transition-all select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                [ngClass]="{
                  'border-iq-investigate':
                    level.key === SeverityLevel.Investigate,
                  'border-iq-monitor': level.key === SeverityLevel.Monitor,
                  'border-iq-observe': level.key === SeverityLevel.Observe,
                  'text-gray-800': level.value(),
                  'opacity-40': !level.value(),
                }"
                [attr.aria-pressed]="level.value()"
                [attr.aria-label]="'Show ' + level.key + ' severity events'"
              >
                {{ level.key }}
              </button>
            }
          </div>
        </div>
      </div>
    </div>

    @if (filteredDevices$().length && !isLoading) {
      <div class="flex flex-col md:flex-row">
        <!-- Left: Device List -->
        <div
          class="border-gray-150 w-full shrink-0 border-b md:max-h-[600px] md:w-72 md:overflow-y-auto md:border-r md:border-b-0"
        >
          <ul class="divide-gray-150 divide-y" role="list" aria-label="Devices">
            @for (device of filteredDevices$(); track device.deviceName) {
              <li>
                <button
                  type="button"
                  class="relative flex w-full cursor-pointer items-center justify-between overflow-hidden px-4 py-3 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-inset"
                  (click)="onSelectDevice(device)"
                  [attr.aria-current]="
                    selectedDevice$()?.deviceName === device.deviceName
                      ? 'true'
                      : null
                  "
                  [attr.aria-expanded]="
                    expandedMobileDevice$()?.deviceName === device.deviceName
                  "
                  [ngClass]="
                    selectedDevice$()?.deviceName === device.deviceName
                      ? 'bg-blue-75 shadow-[inset_3px_0_0_0_#0D62FF]'
                      : 'bg-white hover:bg-gray-50'
                  "
                >
                  <div class="min-w-0 flex-1">
                    <div
                      class="font-lato truncate text-sm font-semibold text-gray-900"
                    >
                      {{ device.deviceName }}
                    </div>
                    <div class="font-lato mt-0.5 text-xs text-gray-600">
                      {{ device.events.length }}
                      {{ device.events.length || 0 | pluralize: 'Event' }}
                      <span class="hidden sm:inline"
                        >&nbsp;·&nbsp;Latest event:
                        {{ device.events[0].date | date: 'MMM d, y' }}</span
                      >
                    </div>
                  </div>
                  <mat-icon
                    class="!flex !h-4 !w-4 !items-center !justify-center !text-base"
                    [ngClass]="
                      selectedDevice$()?.deviceName === device.deviceName
                        ? 'text-blue-500'
                        : 'text-gray-300'
                    "
                    aria-hidden="true"
                    >chevron_right</mat-icon
                  >
                </button>
              </li>
              <!-- Mobile: inline detail under the selected device -->
              @if (expandedMobileDevice$()?.deviceName === device.deviceName) {
                <li class="bg-blue-75/30 p-4 md:hidden">
                  <ng-container *ngTemplateOutlet="deviceDetailTpl" />
                </li>
              }
            }
          </ul>
        </div>

        <!-- Right: Device Details (desktop) -->
        <div
          class="hidden flex-1 p-4 md:block md:p-6"
          aria-live="polite"
          aria-atomic="false"
        >
          <ng-container *ngTemplateOutlet="deviceDetailTpl" />
        </div>
      </div>

      <ng-template #deviceDetailTpl>
        <div class="border-gray-150 mb-4 border-b pb-3">
          <div
            class="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:justify-between"
          >
            <h3 class="font-montserrat text-lg font-semibold text-gray-900">
              {{ selectedDevice$()?.deviceName || 'None selected' }}
            </h3>
            <span class="font-lato shrink-0 text-xs text-gray-600">
              {{ selectedDevice$()?.events.length || 0 }} Observation
              {{
                selectedDevice$()?.events.length || 0
                  | pluralize: 'Summary' : 'Summaries'
              }}
            </span>
          </div>
          <p class="font-lato mt-1 text-xs text-gray-600 italic" role="note">
            (This output is generated using AI and may contain inaccuracies;
            please verify with source data before taking actions)
          </p>
        </div>

        @if (selectedDevice$()?.deviceName) {
          <div class="overflow-x-auto">
            <table
              class="border-gray-150 min-w-full table-auto rounded-md border font-normal"
              [attr.aria-label]="'Events for ' + selectedDevice$()?.deviceName"
            >
              <thead class="bg-gray-200">
                <tr
                  class="text-left text-xs tracking-wider text-gray-600 uppercase"
                >
                  <th class="px-4 py-2" scope="col">Date</th>
                  <th class="px-4 py-2" scope="col">Time</th>
                  <th class="px-4 py-2" scope="col">Severity</th>
                  <th class="px-4 py-2" scope="col">Lead Count</th>
                </tr>
              </thead>
              <tbody class="text-sm text-gray-700">
                @for (
                  event of filteredSelectedDeviceEvents$().slice(
                    pageNumber * 8,
                    (pageNumber + 1) * 8
                  );
                  track $index
                ) {
                  <tr class="border-t border-gray-100">
                    <td class="px-4 py-2">
                      {{ event.date | date: 'MMM d, y' }}
                    </td>
                    <td class="px-4 py-2">
                      {{ event.begin_time }} – {{ event.end_time }}
                    </td>
                    <td class="px-4 py-2">
                      <span
                        class="inline-block w-20 rounded-md px-2 py-1 text-center text-xs font-medium"
                        [ngClass]="{
                          'bg-iq-investigate text-white':
                            event.action_label === SeverityLevel.Investigate,
                          'bg-iq-monitor text-gray-900':
                            event.action_label === SeverityLevel.Monitor,
                          'bg-iq-observe text-gray-900':
                            event.action_label === SeverityLevel.Observe,
                        }"
                      >
                        {{ event.action_label }}
                      </span>
                    </td>
                    <td class="px-4 py-2">{{ event.lead_count }}</td>
                  </tr>
                  <tr class="bg-gray-50 text-gray-900">
                    <td colspan="4" class="px-4 py-2">
                      <div class="flex items-center gap-1.5">
                        <mat-icon
                          fontSet="material-icons-outlined"
                          class="!m-0 !flex !h-4 !w-4 shrink-0 !items-center !justify-center !text-base !leading-4 text-gray-900"
                          aria-hidden="true"
                          >auto_awesome</mat-icon
                        >
                        <div>
                          <span class="sr-only">AI-generated summary: </span>
                          {{ event.summary }}
                        </div>
                      </div>
                    </td>
                  </tr>
                }
              </tbody>
            </table>

            <mat-paginator
              (page)="pageNumber = $event.pageIndex"
              [length]="filteredSelectedDeviceEvents$().length"
              [pageIndex]="pageNumber"
              [pageSize]="8"
              aria-label="Select page of device events"
            ></mat-paginator>
          </div>
        }

        @if (!selectedDevice$()?.deviceName) {
          <p class="text-sm text-gray-600 italic" role="status">
            Select a device to see details.
          </p>
        }
      </ng-template>
    }
  }
</div>
